<script lang="ts">
	import { onMount } from 'svelte';
	import type { Issue, IssueCategory, Report } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { copyText } from '#lib/ui/clipboard';
	import {
		buildRevisionLetter,
		makeEntries,
		REVISION_STATUSES,
		type RevisionEntry,
		type RevisionStatus
	} from '#lib/report/revision';

	interface Props {
		report: Report;
		onclose: () => void;
	}
	let { report, onclose }: Props = $props();

	let entries = $state<RevisionEntry[]>([]);
	let letter = $state('');
	let manual = $state(false);
	let copied = $state(false);

	onMount(() => {
		entries = makeEntries(report);
		letter = buildRevisionLetter(entries);
	});

	const groups = $derived(groupEntries(entries));

	function groupEntries(list: RevisionEntry[]): [IssueCategory, RevisionEntry[]][] {
		const order: IssueCategory[] = [
			'statistics',
			'citations',
			'structure',
			'language',
			'access',
			'provenance'
		];
		const map = new Map<IssueCategory, RevisionEntry[]>();
		for (const entry of list) {
			const bucket = map.get(entry.issue.category) ?? [];
			bucket.push(entry);
			map.set(entry.issue.category, bucket);
		}
		return order.filter((c) => map.has(c)).map((c) => [c, map.get(c)!]);
	}

	function regenerate() {
		letter = buildRevisionLetter(entries);
		manual = false;
	}

	function updateResponse(id: string, value: string) {
		entries = entries.map((e) => (e.issue.id === id ? { ...e, response: value } : e));
		if (!manual) regenerate();
	}

	function updateStatus(id: string, value: RevisionStatus) {
		entries = entries.map((e) => (e.issue.id === id ? { ...e, status: value } : e));
		if (!manual) regenerate();
	}

	function title(issue: Issue): string {
		return issueTextEn(issue)?.title ?? issue.title;
	}

	async function copy() {
		if (await copyText(letter)) {
			copied = true;
			setTimeout(() => (copied = false), 1800);
		}
	}
</script>

<div class="overlay">
	<div class="modal" role="dialog" tabindex="-1" aria-modal="true" aria-label={t('revision.title')}>
		<header class="head">
			<div>
				<p class="eyebrow">{t('revision.eyebrow')}</p>
				<h2>{t('revision.title')}</h2>
				<p class="sub">{t('revision.sub', { n: entries.length })}</p>
			</div>
			<button class="btn btn-ghost btn-sm" onclick={onclose} aria-label={t('common.close')}>✕</button>
		</header>

		<div class="body">
			<section class="editor">
				{#if entries.length === 0}
					<p class="empty">{t('revision.empty')}</p>
				{/if}
				{#each groups as [category, list] (category)}
					<h3>{t(`category.${category}`)} <span class="count">{list.length}</span></h3>
					{#each list as entry (entry.issue.id)}
						<article class="entry">
							<p class="finding"><span class="label">{t('revision.letter.finding')}:</span> {title(entry.issue)}</p>
							<div class="controls">
								<textarea
									rows="2"
									placeholder={t('revision.letter.placeholder')}
									value={entry.response}
									oninput={(e) => updateResponse(entry.issue.id, e.currentTarget.value)}
									aria-label={t('revision.letter.response')}
								></textarea>
								<select
									value={entry.status}
									onchange={(e) => updateStatus(entry.issue.id, e.currentTarget.value as RevisionStatus)}
									aria-label={t('revision.letter.status')}
								>
									{#each REVISION_STATUSES as status (status)}
										<option value={status}>{t(`revision.status.${status}`)}</option>
									{/each}
								</select>
							</div>
						</article>
					{/each}
				{/each}
			</section>

			<section class="preview">
				<div class="preview-head">
					<h3>{t('revision.letterTitle')}</h3>
					{#if manual}<span class="manual">{t('revision.manualHint')}</span>{/if}
				</div>
				<textarea
					class="letter"
					rows="20"
					value={letter}
					oninput={(e) => {
						letter = e.currentTarget.value;
						manual = true;
					}}
					aria-label={t('revision.letterTitle')}
				></textarea>
				<div class="actions">
					<button class="btn btn-primary btn-sm" onclick={copy}>
						{copied ? t('revision.copied') : t('revision.copy')}
					</button>
					<button class="btn btn-sm" onclick={regenerate}>{t('revision.regenerate')}</button>
				</div>
			</section>
		</div>
	</div>
</div>

<style>
	.overlay {
		position: fixed;
		inset: 0;
		background: rgb(28 25 23 / 0.45);
		display: grid;
		place-items: center;
		padding: 1rem;
		z-index: 65;
	}
	.modal {
		width: min(72rem, 100%);
		max-height: 92vh;
		overflow: auto;
		background: var(--paper);
		border: 1px solid var(--line);
		border-radius: var(--radius);
		padding: 1.2rem;
		box-shadow: 0 24px 60px rgb(0 0 0 / 0.22);
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
	}
	.eyebrow {
		margin: 0 0 0.2rem;
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--muted);
		font-weight: 650;
	}
	h2 {
		margin: 0;
		font-size: 1.15rem;
	}
	.sub {
		margin: 0.2rem 0 0;
		color: var(--muted);
		font-size: 0.82rem;
	}
	.body {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		gap: 1.2rem;
		margin-top: 1rem;
	}
	h3 {
		font-family: var(--font-ui);
		font-size: 0.85rem;
		margin: 0.9rem 0 0.45rem;
	}
	.count {
		color: var(--muted);
		font-weight: 400;
	}
	.entry {
		border: 1px solid var(--line);
		border-radius: 9px;
		background: #fff;
		padding: 0.6rem 0.7rem;
		margin-bottom: 0.5rem;
	}
	.finding {
		margin: 0 0 0.4rem;
		font-size: 0.82rem;
		font-weight: 600;
		line-height: 1.35;
	}
	.label {
		color: var(--muted);
		font-weight: 500;
	}
	.controls {
		display: grid;
		gap: 0.35rem;
	}
	textarea,
	select {
		width: 100%;
		font: inherit;
		font-size: 0.8rem;
		border: 1px solid var(--line-strong);
		border-radius: 7px;
		padding: 0.4rem 0.5rem;
		background: var(--paper);
		resize: vertical;
	}
	.letter {
		min-height: 22rem;
		font-family: var(--font-mono, ui-monospace, monospace);
		font-size: 0.78rem;
		line-height: 1.45;
		background: #fff;
	}
	.preview-head {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 0.6rem;
	}
	.preview-head h3 {
		margin-top: 0;
	}
	.manual {
		font-size: 0.7rem;
		color: var(--warning);
	}
	.actions {
		display: flex;
		gap: 0.4rem;
		margin-top: 0.5rem;
		flex-wrap: wrap;
	}
	.empty {
		color: var(--ink-2);
		font-size: 0.85rem;
	}
	@media (max-width: 900px) {
		.body {
			grid-template-columns: 1fr;
		}
		.modal {
			padding: 1rem;
		}
	}
</style>
